import React from 'react';
import PropTypes from 'prop-types';
import ResourceForm from './ResourceForm';
import {clone, isTruthy} from 'utils/DBUtils';
import MultiPlatformComponents from 'components/forms/Component Sets/MultiPlatformComponents';
import ConditionalSection from "components/common/ConditionalSection";
import ViewSwitchSection from 'components/forms/ViewSwitchSection';
import CollectionManagementSection from 'components/forms/CollectionManagementSectionContainer';
import {getBreakResource, isFunction} from "../../utils/DBUtils";
import connectBreakSection from "../../hoc/BreakSection";

class MultiPlatformResourceForm extends ResourceForm {

    /**
     * Creates same resource form but adds current platform to it..
     * @param fieldPath
     * @param resourcePath
     * @return {ResourceFormProxy}
     */
    makeProxy(fieldPath, resourcePath) {
        let proxy = super.makeProxy(fieldPath, resourcePath);
        proxy.currentPlatform = this.props.currentPlatform;
        return proxy;
    }

    /**
     * Splits a field value into a map of platform values.
     * Only platforms that are on this form's resource are set.
     * @override
     * @param fieldPath
     * @return {{}}
     */
    valueForField(fieldPath) {
        let platforms = Object.keys(this.props.resource);
        let master = this.props.resource["MASTER"];
        let platformValues = {};
        for (let platform of platforms) {
            platformValues[platform] = getBreakResource(master, this.props.resource[platform], fieldPath);
        }
        return platformValues;
    }

    /**
     * Returns the value of given field for currentPlatform
     * @override
     * @param fieldPath
     * @return {*}
     */
    currentValueForField(fieldPath) {
        let platformValues = this.valueForField(fieldPath);
        return platformValues[this.props.currentPlatform];
    }

    /**
     * Returns the resource at the given path
     * @param resourcePath
     * @return {*}
     */
    resourceAtPath(resourcePath) {
        if (!resourcePath || resourcePath.length === 0) {
            return this.props.resource
        }
        let platforms = Object.keys(this.props.resource);
        let master = this.props.resource["MASTER"];
        let platformValues = {};
        for (let platform of platforms) {
            platformValues[platform] = getBreakResource(master, this.props.resource[platform], resourcePath);
        }
        return platformValues;
    }

    /**
     * Returns the resource at the given path for the currentPlatform
     * @param resourcePath
     * @return {*}
     */
    currentResourceAtPath(resourcePath) {
        let platformValues = this.resourceAtPath(resourcePath);
        return platformValues[this.props.currentPlatform];
    }

    /**
     * Creates function that takes in a value and platform, then passing them to onValueChanged callback
     * @override
     * @param fieldPath
     * @param resourcePath
     * @return {function(*=, *=)}
     */
    makeOnValueChanged(fieldPath, resourcePath) {
        return (value, platform) => {
            this.props.onValueChanged(resourcePath, fieldPath, value, platform);
        }
    }

    makeCollectionManagerWrapper(field, collection, fieldPath, resourcePath, sectionKeyPath, sectionIndex, topLevel) {
        return (
          <CollectionManagementSection field={field}
                                       topLevel={topLevel}
                                       sectionIndex={sectionIndex}
                                       section={field}
                                       fieldPath={fieldPath}
                                       resourcePath={resourcePath}
                                       sectionKeyPath={sectionKeyPath}
                                       formProxy={this.makeProxy(fieldPath, resourcePath)}/>
        );
    }

    makeViewSwitchSectionWrapper(section, viewCaseFields, fieldPath, resourcePath) {
        let proxy = this.makeProxy(fieldPath, resourcePath);
        return (
          <ViewSwitchSection section={section}
                             formProxy={proxy}
                             switchCases={viewCaseFields}/>
        );
    }

    /**
     * Wraps config component in a resource field that connects it to the store
     * @param field
     * @param fieldPath
     * @param resourcePath
     * @param sectionIndex
     * @param sectionKeyPath
     * @return {*}
     */
    makeComponent(field, fieldPath, resourcePath, sectionIndex, sectionKeyPath) {

        try {
            let Component = this.props.components[field.type];
            if (!Component) {
                // Can't render without a component
                console.warn(`Could not find component type ${field.type} in config`, this.props.components);
                return `Invalid field type ${field.type}`;
            }

            /**
             * For explication why path override is here
             * @see ResourceForm#makeComponent
             */
            if (isFunction(field.pathOverride)) {
                // get new paths, slicing before to prevent accidental modification
                let newPaths = field.pathOverride(fieldPath.slice(), resourcePath.slice());
                fieldPath = newPaths.fieldPath;
                resourcePath = newPaths.resourcePath
            }

            // console.log(`field`, field)
            let key = `${ this.getUniqueComponentKey(field, resourcePath) }`
            let proxy = this.makeProxy(fieldPath, resourcePath);

            return (
                <Component key={key}
                            reducerKey={this.props.reducerKey}
                            fieldKey={key}
                            resourcePath={resourcePath}
                            fieldPath={fieldPath}
                            field={field}
                            formProxy={proxy}
                            sectionKeyPath={sectionKeyPath}
                            sectionIndex={sectionIndex}
                            currentPlatform={this.props.currentPlatform}
                />
            );

        } catch (e) {
            console.error(e.message)
            return e.message
        }
    }

    /**
     * Wraps a conditional section in a resource field that connects it to the store
     * @param field
     * @param index
     * @param key
     * @param fieldPath
     * @param resourcePath
     * @param fields
     * @return {XML}
     */
    makeConditionalSectionWrapper(field, index, key, fieldPath, resourcePath, fields, sectionKeyPath) {
      let proxy = this.makeProxy(fieldPath, resourcePath);

      return (
        <ConditionalSection currentPlatform={this.props.currentPlatform}
                            platform={"MASTER"}
                            reducerKey={this.props.reducerKey}
                            sectionKeyPath={sectionKeyPath}
                            resourcePath={resourcePath}
                            fieldPath={fieldPath}
                            field={field}
                            formProxy={proxy}
                            index={index}
                            key={key}
                            fieldKey={key}>
          {fields}
        </ConditionalSection>
      );
    }

    /**
     * Wraps a section wrapper in a resource section to connect it to the store
     * @param SectionWrapperComponent
     * @param key
     * @param section
     * @param sectionIndex
     * @param fieldPath
     * @param resourcePath
     * @param children
     * @param sectionGroup
     * @return {XML}
     */
    makeSectionWrapper(SectionWrapperComponent, key, section, sectionIndex, fieldPath, resourcePath, children, sectionGroup, sectionKeyPath) {
      let proxy = this.makeProxy(fieldPath, resourcePath);
      const BreakResourceSection = connectBreakSection(SectionWrapperComponent);
      return (
        <BreakResourceSection index={sectionIndex}
                              formProxy={proxy}
                              section={section}
                              reducerKey={this.props.reducerKey}
                              openSection={this.props.openSection}
                              sectionGroup={sectionGroup || null}
                              sectionIndex={sectionIndex}
                              sectionKey={key}>
          {children}
        </BreakResourceSection>
      );
    }

    readyToUpdate() {
        // only update if there is a resource and a master value
        return isTruthy(this.props.resource) && isTruthy(this.props.resource.MASTER);
    }
}

const basePropTypes = clone(ResourceForm.propTypes);

// Change resource type to be a platform value
basePropTypes.resource = PropTypes.shape({
    MASTER: PropTypes.object.isRequired,
    DAILYBREAK: PropTypes.object,
    EMBED: PropTypes.object,
    DEMO: PropTypes.object,
}).isRequired;

basePropTypes.currentPlatform = PropTypes.oneOf(["MASTER", "DAILYBREAK", "EMBED", "DEMO"]);

MultiPlatformResourceForm.propTypes = basePropTypes;

const baseDefaultProps = clone(ResourceForm.defaultProps);

baseDefaultProps.components = MultiPlatformComponents;

MultiPlatformResourceForm.defaultProps = baseDefaultProps;

export default MultiPlatformResourceForm;



// WEBPACK FOOTER //
// ./public/components/forms/MultiPlatformResourceForm.jsx